import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';

interface TopItem {
  name: string;
  value: number;
}

interface ShareCardTemplateProps {
  totalValue: number;
  itemCount: number;
  topItems?: TopItem[];
  userName?: string;
}

export default function ShareCardTemplate({
  totalValue,
  itemCount,
  topItems = [],
  userName,
}: ShareCardTemplateProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          width: 340,
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusLg,
          padding: theme.spacing.xl,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.xl,
        },
        logo: {
          width: 32,
          height: 32,
        },
        brandName: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
        },
        valueSection: {
          alignItems: 'center',
          marginBottom: theme.spacing.xl,
          paddingVertical: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderBottomWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
        },
        totalValue: {
          ...theme.typography.valueLg,
          color: theme.colors.accent,
          marginTop: theme.spacing.xs,
        },
        itemCount: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xs,
        },
        topItemsSection: {
          marginBottom: theme.spacing.xl,
        },
        topItemsLabel: {
          marginBottom: theme.spacing.md,
        },
        topItem: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: theme.spacing.sm,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        topItemRank: {
          width: 24,
          height: 24,
          backgroundColor: theme.colors.accent,
          borderRadius: theme.radius.radiusSm,
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: theme.spacing.md,
        },
        rankText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.onAccent,
        },
        topItemName: {
          flex: 1,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        topItemValue: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        footer: {
          alignItems: 'center',
          paddingTop: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        footerContent: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.xxs,
        },
        footerText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        footerUrl: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
      }),
    [theme],
  );

  const formattedValue = totalValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Image
          source={require('../../public/app/cached-illustration.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.brandName}>Cache</Text>
      </View>

      <View style={styles.valueSection}>
        <Text style={commonStyles.sectionLabel}>My Collection Value</Text>
        <Text style={styles.totalValue}>${formattedValue}</Text>
        <Text style={styles.itemCount}>
          {itemCount} {itemCount === 1 ? 'item' : 'items'} tracked
        </Text>
      </View>

      {topItems.length > 0 && (
        <View style={styles.topItemsSection}>
          <Text style={[commonStyles.sectionLabel, styles.topItemsLabel]}>Top Items</Text>
          {topItems.slice(0, 3).map((item, index) => (
            <View key={index} style={styles.topItem}>
              <View style={styles.topItemRank}>
                <Text style={styles.rankText}>{index + 1}</Text>
              </View>
              <Text style={styles.topItemName} numberOfLines={1}>
                {item.name}
              </Text>
              <Text style={styles.topItemValue}>
                ${item.value.toLocaleString()}
              </Text>
            </View>
          ))}
        </View>
      )}

      <View style={styles.footer}>
        <View style={styles.footerContent}>
          <Ionicons name="sparkles" size={14} color={theme.colors.accent} />
          <Text style={styles.footerText}>Powered by Cache</Text>
        </View>
        <Text style={styles.footerUrl}>discovercache.com</Text>
      </View>
    </View>
  );
}
